import { ArrowDown } from 'lucide-react';

import {
  AchievementRates,
  AlumniCommunity,
  AndragogicalPrinciples,
  CampusThalwil,
  ExperienceGallery,
  InternshipProgramme,
} from '@/components/experience';
import {
  MediaFrame,
  NavyAtmosphere,
  PointerParallax,
  ScrollReveal,
  SplitReveal,
  TextStagger,
  TiltCard,
} from '@/components/motion';
import { EnquiryForm } from '@/components/sections';
import { Button } from '@ibas/ui';

/**
 * Real IBAS still: a student pausing beneath a tree on the green campus grounds.
 * A portrait orientation is used so the figure fills the 4/5 hero plate under
 * object-cover. A still is used deliberately here — the one on-brand education
 * clip is already the Admissions hero, and the page carries its motion through
 * the sections (parallax, counters, charts, reveals) rather than a loop.
 */
const HERO_POSTER = '/media/images/graduation-events/student-candid-outdoor-portrait.webp';

/**
 * ExperiencePage (/experience) — "IBAS Experience". A navy, motion-led hero
 * opens the section, then the six requested blocks follow in order:
 * Campus Thalwil · Student Achievement Rates ★ · Andragogical Principles ★ ·
 * Internship · Alumni · Gallery. The shared enquiry form closes the page.
 *
 * Two sections carry accreditation-mandated content: the ACBSP Student
 * Achievement Rates and the verbatim Andragogical Principles. Each in-page
 * section is anchored (id + scroll-mt) so the mega-nav can deep-link to it.
 * Every section is a self-contained, reduced-motion-safe component reading only
 * verified facts from @/data/experience.
 */
export function ExperiencePage() {
  return (
    <div className="bg-background">
      {/* ── HERO ─────────────────────────────────────────────────────────── */}
      <section className="relative isolate flex min-h-[88vh] items-center overflow-hidden bg-brand-navy">
        <NavyAtmosphere />

        <div className="relative z-10 mx-auto grid w-full max-w-6xl grid-cols-1 items-center gap-12 px-6 py-20 lg:grid-cols-[1.05fr_0.95fr] lg:gap-16">
          <div className="max-w-xl">
            <TextStagger
              text="IBAS Experience"
              className="font-label text-xs tracking-[0.24em] text-brand-gold-light uppercase"
            />
            <SplitReveal
              as="h1"
              by="lines"
              className="mt-4 font-display text-4xl leading-[1.05] text-white md:text-6xl"
            >
              More than a qualification. A place to belong.
            </SplitReveal>
            <ScrollReveal from="up" delay={0.1}>
              <p className="mt-6 max-w-lg text-lg leading-relaxed text-white/70">
                From the Thalwil campus to a global alumni community, the IBAS experience is built on
                proven achievement, an adult-centred way of learning, and real routes into
                international work. Explore what studying with IBAS is like.
              </p>
            </ScrollReveal>
            <ScrollReveal from="up" delay={0.2}>
              <div className="mt-8 flex flex-wrap items-center gap-4">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white"
                  asChild
                >
                  <a href="#achievement-rates">
                    See our achievement rates
                    <ArrowDown aria-hidden className="size-4" />
                  </a>
                </Button>
              </div>
            </ScrollReveal>
          </div>

          {/* Hero media plate — pointer parallax over a tilt-reactive frame. */}
          <PointerParallax strength={16} className="mx-auto w-full max-w-md lg:max-w-none">
            <TiltCard className="rounded-2xl" glare>
              <div data-parallax data-depth="0.8">
                <MediaFrame
                  image={HERO_POSTER}
                  alt="An IBAS student pausing beneath a tree on the green campus grounds near Zurich on a sunny day."
                  aspect="aspect-[4/5]"
                  overlay={55}
                  rounded="rounded-2xl"
                  position="50% 45%"
                >
                  <div className="absolute inset-x-6 bottom-6 md:inset-x-8 md:bottom-8">
                    <span className="font-label text-xs tracking-[0.2em] text-brand-gold-light uppercase">
                      Study Your Way. Connect Globally.
                    </span>
                    <p className="mt-1 font-display text-2xl leading-tight text-white md:text-3xl">
                      Learning, community and achievement.
                    </p>
                  </div>
                </MediaFrame>
              </div>
            </TiltCard>
          </PointerParallax>
        </div>
      </section>

      {/* ── 1 · CAMPUS — THALWIL ──────────────────────────────────────────── */}
      <div id="campus" className="scroll-mt-24">
        <CampusThalwil />
      </div>

      {/* ── 2 · STUDENT ACHIEVEMENT RATES ★ ───────────────────────────────── */}
      <div id="achievement-rates" className="scroll-mt-24">
        <AchievementRates />
      </div>

      {/* ── 3 · ANDRAGOGICAL PRINCIPLES ★ ─────────────────────────────────── */}
      <div id="andragogy" className="scroll-mt-24">
        <AndragogicalPrinciples />
      </div>

      {/* ── 4 · INTERNSHIP PROGRAMME ──────────────────────────────────────── */}
      <div id="internship" className="scroll-mt-24">
        <InternshipProgramme />
      </div>

      {/* ── 5 · ALUMNI ────────────────────────────────────────────────────── */}
      <div id="alumni" className="scroll-mt-24">
        <AlumniCommunity />
      </div>

      {/* ── 6 · GALLERY ───────────────────────────────────────────────────── */}
      <div id="gallery" className="scroll-mt-24">
        <ExperienceGallery />
      </div>

      {/* Shared enquiry form closes the page. */}
      <EnquiryForm />
    </div>
  );
}
